import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';

// Auth
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout
import { MainLayout } from './components/layout';

// Pages
import {
  ExecutiveOverview,
  DealPipeline,
  DealDetail,
  DealFormPage,
  QuickAnalyzer,
  DealAnalyzer,
  RenovationControl,
  MarketComparables,
  ExitTracker,
  PartnerScorecard,
  AIIntelligence,
  DataIngestion,
  Help,
  Settings,
  Login,
  UserManagement,
  Profile,
  Licitor,
} from './pages';

// API
import { dealsApi } from './utils/api';
import { formatCurrencyCompact, formatPercent } from './utils/formatters';

// Remove Emergent badge
const removeBadge = () => {
  const observer = new MutationObserver(() => {
    document.querySelectorAll('*').forEach(el => {
      if (el.textContent?.includes('Made with Emergent') && el.closest) {
        const badge = el.closest('a') || el.closest('div[style*="fixed"]');
        if (badge) badge.remove();
      }
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => {
    document.querySelectorAll('a[href*="emergent"]').forEach(el => {
      if (el.textContent?.includes('Made with')) el.remove();
    });
  }, 100);
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Content (wrapped in AuthProvider)
function AppContent() {
  const { isAuthenticated, loading, user } = useAuth();
  const [stats, setStats] = useState(null);

  // Remove badge on mount
  useEffect(() => {
    removeBadge();
  }, []);

  // Fetch global stats for top bar (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      setStats(null);
      return;
    }

    const fetchStats = async () => {
      try {
        const data = await dealsApi.getStats();
        const activeDeals = Object.entries(data.deals_by_status || {})
          .filter(([status]) => !['sold', 'abandoned'].includes(status))
          .reduce((sum, [, count]) => sum + count, 0);

        setStats({
          activeDeals,
          capitalDeployed: formatCurrencyCompact(data.total_capital_deployed),
          avgRoi: formatPercent(data.average_roi),
        });
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public route - Login */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout stats={stats} user={user}>
              <Routes>
                <Route path="/" element={<ExecutiveOverview />} />
                <Route path="/pipeline" element={<DealPipeline />} />
                <Route path="/deal/new" element={<DealFormPage />} />
                <Route path="/deal/:dealId/edit" element={<DealFormPage />} />
                <Route path="/deal/:dealId" element={<DealDetail />} />
                <Route path="/deal/:dealId/renovation" element={<RenovationControl />} />
                <Route path="/analyzer" element={<DealAnalyzer />} />
                <Route path="/quick-analyzer" element={<QuickAnalyzer />} />
                <Route path="/renovation" element={<RenovationControl />} />
                <Route path="/comparables" element={<MarketComparables />} />
                <Route path="/exit" element={<ExitTracker />} />
                <Route path="/partners" element={<PartnerScorecard />} />
                <Route path="/intelligence" element={<AIIntelligence />} />
                <Route path="/data-ingestion" element={<DataIngestion />} />
                <Route path="/licitor" element={<Licitor />} />
                <Route path="/help" element={<Help />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Profile />} />
                
                {/* Admin-only route */}
                <Route
                  path="/users"
                  element={
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  }
                />
                
                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <Toaster 
          position="bottom-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'IBM Plex Sans, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
